import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession, signOut } from '../../auth/session';
import ThemeToggle from '../../components/ThemeToggle';

type Tab = [string, string, boolean?];
interface Group { label: string; items: Tab[]; collapsible?: boolean }
// Trimmed deliberately — the Dashboard's own "Quick access" grid now covers everything else
// (Trends, Readings, Daily logbook, Audit trail, PMS, Complaints, Materials report, and every
// item in Setup below). The sidebar only needs to get you to the dashboard, and to the
// less-frequently-visited config screens when you're already deep in another page.
const groups: Group[] = [
  {
    label: 'Monitor',
    items: [
      ['/admin', 'Dashboard', true],
    ],
  },
  {
    label: 'Setup',
    collapsible: true,
    items: [
      ['/admin/equipment', 'Equipment'],
      ['/admin/tags', 'NFC tags'],
      ['/admin/templates', 'Form templates'],
      ['/admin/thresholds', 'Thresholds'],
      ['/admin/operators', 'Operators'],
      ['/admin/settings', 'Settings'],
    ],
  },
];

const isIn = (path: string, items: Tab[]) => items.some(([to]) => path === to || path.startsWith(to + '/'));

export default function AdminLayout() {
  const session = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const setupGroup = groups.find((g) => g.collapsible)!;
  const [setupOpen, setSetupOpen] = useState(() => isIn(loc.pathname, setupGroup.items));
  // auto-expand when the user navigates onto a Setup page
  useEffect(() => {
    if (isIn(loc.pathname, setupGroup.items)) setSetupOpen(true);
  }, [loc.pathname, setupGroup.items]);

  // close the mobile drawer whenever the route changes
  useEffect(() => setOpen(false), [loc.pathname]);
  // lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <div className="adminShell">
      <header className="adminTopbar">
        <button className="hamburger" aria-label="Open menu" onClick={() => setOpen(true)}>☰</button>
        <strong>UNM Electrical · Admin</strong>
        <span className="who">{session?.operatorName}</span>
      </header>

      {open && <div className="drawerBackdrop" onClick={() => setOpen(false)} />}

      <aside className={`adminSidebar ${open ? 'open' : ''}`}>
        <div className="sidebarHead">
          <strong>UNM Electrical · Admin</strong>
          <button className="closeBtn" aria-label="Close menu" onClick={() => setOpen(false)}>✕</button>
        </div>
        <nav>
          {groups.map((g) => {
            const links = g.items.map(([to, label, end]) => (
              <NavLink key={to} to={to} end={!!end} className={({ isActive }) => (isActive ? 'active' : '')}>
                {label}
              </NavLink>
            ));
            if (!g.collapsible) {
              return (
                <div key={g.label} className="navgroup">
                  <div className="navgroup-label">{g.label}</div>
                  {links}
                </div>
              );
            }
            // collapsed by default; user can toggle, and it auto-opens on a Setup page
            return (
              <details
                key={g.label}
                className="navgroup"
                open={setupOpen}
                onToggle={(e) => setSetupOpen(e.currentTarget.open)}
              >
                <summary className="navgroup-label">{g.label}</summary>
                {links}
              </details>
            );
          })}
        </nav>
        <div className="sidebarFoot">
          <button className="ghost" style={{ width: '100%', justifyContent: 'flex-start', padding: '6px 8px', marginBottom: 4 }}
            onClick={() => nav('/admin/account')}>
            👤 {session?.operatorName}{session?.role === 'admin' ? ' · admin' : ''}
          </button>
          <div className="spread" style={{ marginBottom: 8 }}>
            <ThemeToggle />
            <button className="secondary" style={{ width: 'auto', minHeight: 34 }} onClick={() => { signOut(); nav('/signin'); }}>Sign out</button>
          </div>
        </div>
      </aside>

      <main className="adminContent">
        <Outlet />
      </main>
    </div>
  );
}
